'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';

import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { BreadcrumbsSchema } from '@eliadmin/core/src/components/seo/breadcrumbs-schema';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getFlatBreadcrumbs, getSlugFromPage } from '@eliadmin/core/src/utils/url-utils';
import { usePathname } from 'next/navigation';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  /** Label of the first item (dictionary key or text), „Domov“ by default. */
  homeText?: string;
  breadcrumb: Breadcrumb;
};

const LINK =
  'ea-breadcrumbs__link rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]';

/**
 * DLZ breadcrumb (Figma „Breadcrumbs“, e.g. 60091:1427): DLZ/Body/S, the trail in text/tertiary,
 * caret 16 in icon/placeholder between the items, the last item – the current page in every design
 * – in SemiBold text/primary with `aria-current`. No background; the horizontal padding equals the
 * container gutters, because core pages render the trail outside their `Container`. Long trails
 * wrap. Same API and structured data as the core component.
 */
export function Breadcrumbs({ breadcrumb, className, homeText }: BreadcrumbsProps) {
  const pathname = usePathname();
  const { t } = useTranslations();

  if (BREADCRUMBS_CONFIG.noDisplay.some(path => path === pathname)) return null;

  const items = getFlatBreadcrumbs(breadcrumb);

  return (
    <>
      <BreadcrumbsSchema breadcrumb={breadcrumb} />
      <nav
        aria-label="Navigačná cesta"
        className={cn('ea-breadcrumbs w-full px-4 md:px-6 lg:px-8', className?.wrapper)}
      >
        <ol
          className={cn(
            'ea-breadcrumbs__list dlz-body-s flex flex-wrap items-center gap-x-2 gap-y-1 text-[#6B7079]',
            className?.list,
          )}
        >
          <li className={cn('ea_breadcrumbs__item', className?.item)}>
            <Link
              href={getSlugFromPage('home') ?? '/'}
              prefetch={false}
              className={cn(LINK, 'transition-colors hover:text-[#0F1012]', className?.link)}
            >
              {t(homeText ?? 'text160')}
            </Link>
          </li>
          {items.map((item, index) => {
            const current = index === items.length - 1;

            return (
              <li
                key={`${item.slug}-${index}`}
                className={cn(
                  'ea_breadcrumbs__item flex min-w-0 items-center gap-2',
                  className?.item,
                  current &&
                    cn(
                      'ea_breadcrumbs__item--active dlz-body-s-medium text-[#0F1012]',
                      // On phones the current page takes the rest of the row and is cut with „…“.
                      'max-sm:min-w-36 max-sm:flex-1 max-sm:basis-0',
                      className?.itemActive,
                    ),
                )}
              >
                <Icon name="caret-right" size={16} className="shrink-0 text-[#B9BDC3]" />
                {item.slug ? (
                  <Link
                    href={item.slug}
                    prefetch={false}
                    aria-current={current ? 'page' : undefined}
                    className={cn(
                      LINK,
                      'min-w-0',
                      current ? 'max-sm:truncate' : 'transition-colors hover:text-[#0F1012]',
                      className?.link,
                    )}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <span aria-current="page" className="min-w-0 max-sm:truncate">
                    {item.name}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
